/**
 * TanStack Query Client Configuration
 *
 * Centralized query client setup for API response caching (GW-227).
 * Implements stale-while-revalidate pattern with configurable cache times.
 */

import { QueryClient } from '@tanstack/react-query';

// ============================================================================
// Stale Time Constants (in milliseconds)
// ============================================================================

/**
 * Cache duration constants for different data types.
 * Based on how frequently each type of data changes.
 */
export const STALE_TIMES = {
  /** Applications: 5 minutes - relatively static catalog data */
  APPLICATIONS: 5 * 60 * 1000,
  /** Sessions: 1 minute - frequently changing state */
  SESSIONS: 1 * 60 * 1000,
  /** Organizations: 5 minutes - relatively static org data */
  ORGANIZATIONS: 5 * 60 * 1000,
  /** Current User: 10 minutes - rarely changes during session */
  CURRENT_USER: 10 * 60 * 1000,
} as const;

// ============================================================================
// Query Key Constants
// ============================================================================

/**
 * Standardized query keys for consistent cache management.
 * Use these keys for invalidation and prefetching.
 */
export const QUERY_KEYS = {
  APPLICATIONS: 'applications',
  SESSIONS: 'sessions',
  ORGANIZATIONS: 'organizations',
  CURRENT_USER: 'currentUser',
} as const;

// ============================================================================
// Query Client Instance
// ============================================================================

/**
 * Singleton QueryClient instance with default configuration.
 *
 * Default options:
 * - retry: false - Don't automatically retry failed requests
 * - refetchOnWindowFocus: false - Don't refetch when window regains focus
 *
 * These defaults ensure predictable behavior and avoid unnecessary requests.
 * Individual queries can override these settings as needed.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});
